import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from "react";
import ServiceOccurenceInfoTab from "../../ServiceOccurenceTabs/ServiceOccurenceInfoTab";
import ServiceOccurenceNotesTab from "../../ServiceOccurenceTabs/ServiceOccurenceNotesTab";
import ServiceOccurenceMediasTab from "../../ServiceOccurenceTabs/ServiceOccurenceMediasTab";
import styles from "./ServiceOccurenceForm.module.css";
import ServiceOccurenceService from "../../../Services/serviceOccurenceService";
import i18n from "../../../Services/i18n";
import { useToasts } from "../../../LayoutComponents/ToastContext";

const Tabs = {
  INFO: "info",
  MEDIA: "media",
  NOTES: "notes",
};

const TAB_LABELS = {
  [Tabs.INFO]: i18n.t("NoteQuickForm.Tabs.Infos"),
  [Tabs.MEDIA]: i18n.t("NoteQuickForm.Tabs.Medias"),
  [Tabs.NOTES]: i18n.t("NoteQuickForm.Tabs.Notes"),
};

const ServiceOccurenceForm = forwardRef((props, ref) => {
  const [activeTab, setActiveTab] = useState(Tabs.INFO);
  const [serviceOccurence, setServiceOccurence] = useState(props.serviceOccurence ?? null);
  const [isRefreshNeeded, setIsRefreshNeeded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const resetPendingRef = useRef(false);
  const ServiceOccurenceFormRef = useRef(null);
  const didInitRef = useRef(false);

  const { addToast } = useToasts();

  useEffect(() => {
    setServiceOccurence(props.serviceOccurence ?? null);
  }, [props.serviceOccurence]);

  useEffect(() => {
    if (props.isNew) {
      didInitRef.current = true;
      return;
    }

    if (serviceOccurence?.externalId) {
      didInitRef.current = true;
    }
  }, [props.isNew, serviceOccurence]);

  useEffect(() => {
    if (resetPendingRef.current && ServiceOccurenceFormRef.current?.reset) {
      ServiceOccurenceFormRef.current.reset(resetPendingRef.current);
      resetPendingRef.current = false;
      setIsDirty(false);
      props.onDirtyChange?.(false);
      didInitRef.current = true;
    }
  }, [serviceOccurence, props]);

  useImperativeHandle(ref, () => ({
    isDirty() {
      return isDirty;
    },
    isRefreshNeeded() {
      return isRefreshNeeded;
    },
    getFormData() {
      return {
        formData: ServiceOccurenceFormRef.current.getFormData(),
        serviceOccurence,
      };
    },
    validate() {
      return new Promise((resolve) => {
        setTimeout(() => {
          const data = ServiceOccurenceFormRef.current.getFormData();

          if (data.mainNote.length > 40000) {
            addToast(
              i18n.t("ClientForm.Validation.InvalidDescriptionLen"),
              "clientFormValidationEmail",
              "ERROR",
            );
            resolve(false);
            return;
          }

          resolve(true);
        }, 250);
      });
    },
    saved: async (serviceOccurenceExternalId) => {
      const so = await ServiceOccurenceService.GetDetailServiceOccurence(
        serviceOccurenceExternalId,
      );

      resetPendingRef.current = so;
      setServiceOccurence(so);
      setIsRefreshNeeded(false);
      setIsDirty(false);
      props.onDirtyChange?.(false);
      didInitRef.current = true;

      return so;
    },
  }));

  const getFilteredService = () => {
    return props.services.filter((x) => {
      if (props.horse?.externalId === -1) return x.isForClients;
      if (props.horse) return x.isForHorses;
      return false;
    });
  };

  const reloadDetail = async (serviceOccurenceExternalId) => {
    const so = await ServiceOccurenceService.GetDetailServiceOccurence(serviceOccurenceExternalId);
    setServiceOccurence(so);
    setIsRefreshNeeded(true);
    setIsDirty(false);
    props.onDirtyChange?.(false);
    didInitRef.current = true;
  };

  const canAccessSecondaryTabs = !!serviceOccurence?.externalId && !isDirty;

  const handleTabChange = (tabKey) => {
    if (!canAccessSecondaryTabs && tabKey !== Tabs.INFO) {
      addToast(i18n.t("Generic.ToastMessage.MustSaveFirst"), "MUSTSAVEFIRST", "WARNING", true);
      return;
    }

    setActiveTab(tabKey);
    props.onTabChange?.(tabKey);
  };

  const renderTabs = () => {
    return (
      <div className={styles.tabs}>
        {Object.values(Tabs).map((tabKey) => {
          const isDisabled = !canAccessSecondaryTabs && tabKey !== Tabs.INFO;
          const className = isDisabled
            ? styles.disabledTab
            : activeTab === tabKey
              ? styles.activeTab
              : styles.tab;

          return (
            <div
              key={tabKey}
              className={className}
              style={{
                transition:
                  "background-color 0.2s ease, " +
                  "border-bottom 0.2s ease, border-top 0.2s ease, border-left 0.2s ease, border-right 0.2s ease, " +
                  "font-weight 0.2s ease, padding 0.2s ease",
              }}
              onClick={() => handleTabChange(tabKey)}>
              {TAB_LABELS[tabKey]}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case Tabs.INFO:
        return (
          <div>
            <ServiceOccurenceInfoTab
              client={props.client}
              horse={props.horse}
              isNew={props.isNew}
              services={getFilteredService()}
              serviceOccurence={serviceOccurence}
              ref={ServiceOccurenceFormRef}
              onDirtyChange={(val) => {
                if (!didInitRef.current) return;
                const dirty = !!val;
                setIsDirty(dirty);
                props.onDirtyChange?.(dirty);
              }}
            />
          </div>
        );

      case Tabs.NOTES:
        return (
          <div>
            <ServiceOccurenceNotesTab
              service={serviceOccurence}
              reloadDetail={(soId) => reloadDetail(soId)}
            />
          </div>
        );

      case Tabs.MEDIA:
        return (
          <div>
            <ServiceOccurenceMediasTab
              service={serviceOccurence}
              reloadDetail={(soId) => reloadDetail(soId)}
            />
          </div>
        );

      default:
        return <div></div>;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.clientInfoSection}>
        {renderTabs()}
        <div className={styles.tabContent}>{renderTabContent()}</div>
      </div>
    </div>
  );
});

export default ServiceOccurenceForm;
